<template>
  <div class="relative">
    <!-- Selection Controls -->
    <div class="mb-4">
      <div class="flex items-center justify-between">
        <label class="flex items-center gap-2">
          <input
            type="checkbox"
            :checked="isAllPagesSelected"
            :indeterminate="isSomePagesSelected"
            class="h-6 w-6 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
            @change="toggleAllPages"
          />
          <span class="text-md font-medium text-gray-700">{{ t('selectAllPages') }}</span>
        </label>

        <div
          v-if="selectedPageUuids.length > 0"
          class="flex items-center gap-3 rounded-lg px-4 py-2"
        >
          <DropdownMenu>
            <DropdownMenuTrigger>
              <button
                class="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium
                  text-gray-700 transition-colors hover:border-gray-300 hover:bg-gray-50"
              >
                <ArrowRight class="h-4 w-4" />
                {{ t('moveTo') }}
                <ChevronDown class="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent class="max-w-lg py-4">
              <DropdownMenuItem
                class="mx-2 mb-2 flex items-center gap-2 rounded-md border border-gray-200 py-2 transition-colors
                  hover:border-gray-300 hover:bg-gray-50"
                @click="createNewDocumentGroupWithSelected"
              >
                <Plus class="h-4 w-4" />
                {{ t('createNewDocument') }}
              </DropdownMenuItem>

              <DropdownMenuItem
                v-for="targetDoc in availableDocumentGroups"
                :key="targetDoc.name"
                class="py-3"
                @click="moveSelectedPagesToDocument(targetDoc.name)"
              >
                {{ targetDoc.name }}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <button
            :disabled="selectedPageUuids.length === 0"
            class="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium
              text-red-600 transition-colors hover:border-red-300 hover:bg-red-50 disabled:cursor-not-allowed
              disabled:opacity-50"
            @click="deleteSelectedPages"
          >
            <Trash2 class="h-4 w-4" />
            {{ t('deletePages') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Page Grid -->
    <div
      class="pt-2"
      @drop="onDocumentDrop"
      @dragover="(event) => event.preventDefault()"
    >
      <div
        ref="scrollContainerRef"
        class="flex gap-12 overflow-x-auto pb-5 pl-16 pr-16"
      >
        <div
          v-for="(page, pageIndex) in document.pages"
          :key="page.uuid"
          class="relative"
        >
          <!-- Drop Zone - Left side (before page) -->
          <div
            v-if="pageIndex === 0"
            class="pointer-events-none absolute -left-6 bottom-0 top-0 border-l-2 transition-all duration-200"
            :class="[
              isDragging &&
              (dragMode === 'reorder' || dragMode === 'move') &&
              ((dragOverTarget?.docIndex === documentIndex &&
                dragOverTarget?.pageIndex === pageIndex &&
                dragOverTarget?.dropZone === 'left') ||
                (proximityDropZone?.docIndex === documentIndex &&
                  proximityDropZone?.pageIndex === pageIndex &&
                  proximityDropZone?.dropZone === 'left'))
                ? 'border-solid border-purple-500'
                : 'border-dashed border-gray-300'
            ]"
          ></div>

          <!-- Invisible drop target - Left side -->
          <div
            v-if="
              pageIndex === 0 &&
              isDragging &&
              ((dragMode === 'reorder' && dragSourceDocument === documentIndex) ||
                dragMode === 'move')
            "
            class="absolute -left-6 bottom-0 top-0 w-24"
            @dragover="onDropZoneDragOver($event, documentIndex, pageIndex, 'left')"
            @dragenter="onDropZoneDragEnter($event, documentIndex, pageIndex, 'left')"
            @dragleave="onDropZoneDragLeave($event, documentIndex, pageIndex, 'left')"
            @drop="onDropZoneDrop($event, documentIndex, pageIndex, 'left')"
          ></div>

          <!-- Drop Zone - Right side (after page) -->
          <div
            class="pointer-events-none absolute -right-6 bottom-0 top-0 border-l-2 transition-all duration-200"
            :class="[
              isDragging &&
              (dragMode === 'reorder' || dragMode === 'move') &&
              ((dragOverTarget?.docIndex === documentIndex &&
                dragOverTarget?.pageIndex === pageIndex &&
                dragOverTarget?.dropZone === 'right') ||
                (proximityDropZone?.docIndex === documentIndex &&
                  proximityDropZone?.pageIndex === pageIndex &&
                  proximityDropZone?.dropZone === 'right'))
                ? 'border-solid border-purple-500'
                : 'border-dashed border-gray-300'
            ]"
          ></div>

          <!-- Invisible drop target - Right side -->
          <div
            v-if="
              isDragging &&
              ((dragMode === 'reorder' && dragSourceDocument === documentIndex) ||
                dragMode === 'move')
            "
            class="absolute -right-6 bottom-0 top-0 w-24"
            @dragover="onDropZoneDragOver($event, documentIndex, pageIndex, 'right')"
            @dragenter="onDropZoneDragEnter($event, documentIndex, pageIndex, 'right')"
            @dragleave="onDropZoneDragLeave($event, documentIndex, pageIndex, 'right')"
            @drop="onDropZoneDrop($event, documentIndex, pageIndex, 'right')"
          ></div>

          <div
            :data-page-uuid="page.uuid"
            :class="[
              'relative h-56 w-36 cursor-move overflow-hidden rounded-md bg-white shadow-sm transition-all',
              selectedPageUuids.includes(page.uuid)
                ? 'border-2 border-purple-500'
                : 'border border-gray-200 hover:shadow-md',

              isDragging && dragMode === 'move'
                ? 'cursor-copy'
                : isDragging && dragMode === 'reorder'
                  ? 'cursor-move'
                  : 'cursor-move'

              // Add visual feedback for multi-page drag
              // isDragging && selectedPageUuids.length > 1 && selectedPageUuids.includes(page.uuid)
              //   ? 'ring-2 ring-purple-400 ring-offset-2 transform scale-105'
              //   : ''
            ]"
            draggable="true"
            @dragstart="onDragStart($event, documentIndex, pageIndex)"
            @dragend="onDragEnd"
          >
            <input
              type="checkbox"
              :checked="selectedPageUuids.includes(page.uuid)"
              class="absolute left-2 top-2 z-20 h-6 w-6 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              @change="togglePageSelection(page.uuid)"
            />

            <!-- Multi-page drag indicator -->
            <div
              v-if="
                isDragging && selectedPageUuids.length > 1 && selectedPageUuids.includes(page.uuid)
              "
              class="absolute right-2 top-2 z-20 flex h-6 w-6 items-center justify-center rounded-full bg-purple-600
                text-xs font-bold text-white shadow-lg"
            >
              {{ selectedPageUuids.length }}
            </div>

            <div class="relative flex h-full w-full items-center justify-center bg-gray-100">
              <div class="group relative h-full w-full">
                <LazyPDFPageThumbnail
                  :thumbnails="page.thumbnails || []"
                  :page-number="page.actualPageNumber || 1"
                  load-margin="100px"
                  class="h-full w-full"
                  :style="{
                    transform: `rotate(${getPageRotation(page.uuid)}deg)`,
                    transition: 'transform 0.3s ease-in-out'
                  }"
                />

                <div
                  class="pointer-events-none absolute inset-0 bg-gray-200/50 opacity-0 transition-opacity duration-150
                    group-hover:opacity-100"
                ></div>
                <div
                  class="absolute bottom-10 left-1/2 flex -translate-x-1/2 items-center gap-2 opacity-0 transition-opacity
                    duration-150 group-hover:opacity-100"
                >
                  <button
                    class="flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 bg-white/90 text-gray-700
                      shadow-sm transition-all hover:bg-white hover:shadow-md"
                    :title="t('previewPage')"
                    @click.stop="previewPage(page)"
                  >
                    <Eye class="h-3.5 w-3.5" />
                  </button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        class="flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 bg-white/90 text-gray-700
                          shadow-sm transition-all hover:bg-white hover:shadow-md"
                        :title="t('moreOptions')"
                      >
                        <MoreHorizontal class="h-4 w-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="start"
                      class="w-56"
                    >
                      <DropdownMenuItem
                        class="py-3"
                        @click="downloadSpecificPage(page, document.name, props.pageRotations)"
                      >
                        <div class="flex items-center gap-2">
                          <Download class="h-4 w-4" />
                          <span>{{ t('downloadPage') }}</span>
                        </div>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        class="py-3 text-red-600 hover:bg-red-50 hover:text-red-700"
                        @click="deleteSpecificPage(page.uuid)"
                      >
                        <div class="flex items-center gap-2">
                          <Trash2 class="h-4 w-4" />
                          <span>{{ t('deletePage') }}</span>
                        </div>
                      </DropdownMenuItem>
                      <div
                        v-if="document.pages.length > 1"
                        class="relative flex cursor-pointer items-center rounded-md px-2 py-3 text-sm transition-colors
                          hover:bg-gray-100"
                      >
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <div class="flex w-full items-center gap-2 p-3">
                              <span class="inline-flex items-center">{{ t('moveTo') }}</span>
                              <ChevronDown class="ml-auto inline-flex h-4 w-4" />
                            </div>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            class="max-w-lg py-4"
                            :side="'right'"
                            :align="'start'"
                            :sideOffset="5"
                          >
                            <DropdownMenuItem
                              class="mx-2 mb-2 flex items-center gap-2 rounded-md border border-gray-200 py-2 transition-colors
                                hover:border-gray-300 hover:bg-gray-50"
                              @click="createNewDocumentGroupWithPage(page.uuid)"
                            >
                              <Plus class="h-4 w-4" />
                              {{ t('createNewDocument') }}
                            </DropdownMenuItem>

                            <DropdownMenuItem
                              v-for="targetDoc in availableDocumentGroups"
                              :key="targetDoc.name"
                              class="py-3"
                              @click="movePageToDocument(page.uuid, targetDoc.name)"
                            >
                              {{ targetDoc.name }}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div class="absolute bottom-0 left-0 right-0 bg-white/90 p-2 backdrop-blur-sm">
                  <div class="flex items-center justify-between">
                    <p class="flex-1 truncate text-xs text-gray-700">{{ page.filename }}</p>
                    <p class="ml-2 text-xs text-gray-600">{{ t('page') }} {{ pageIndex + 1 }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="mt-2 text-center">
            <p class="text-sm text-gray-500">{{ pageIndex + 1 }}</p>
          </div>

          <div
            v-if="pageIndex === 0"
            class="absolute left-[-2.2rem] top-1/2 z-10 flex -translate-y-1/2 flex-col items-center gap-2"
          >
            <button
              :disabled="document.pages.length < 2 || pageIndex === 0"
              class="flex h-6 w-6 items-center justify-center rounded border border-gray-300 bg-white text-black
                shadow-sm hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
              :title="t('splitDocument')"
              @click.stop="splitDocument(pageIndex)"
            >
              <Scissors class="h-4 w-4" />
            </button>
            <button
              class="flex h-6 w-6 items-center justify-center rounded border border-gray-300 bg-white text-black
                transition-colors hover:bg-gray-100 hover:text-gray-600"
              :title="t('addDocuments')"
              @click="openAddDocumentsDialog(pageIndex, 'left')"
            >
              <Plus class="h-4 w-4" />
            </button>
          </div>

          <div
            class="absolute right-[-2.2rem] top-1/2 z-10 flex -translate-y-1/2 flex-col items-center gap-2"
          >
            <button
              :disabled="document.pages.length < 2 || pageIndex === document.pages.length - 1"
              class="flex h-6 w-6 items-center justify-center rounded border border-gray-300 bg-white text-black
                shadow-sm hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
              :title="t('splitDocument')"
              @click.stop="splitDocument(pageIndex)"
            >
              <Scissors class="h-4 w-4" />
            </button>
            <button
              class="flex h-6 w-6 items-center justify-center rounded border border-gray-300 bg-white text-black
                transition-colors hover:bg-gray-100 hover:text-gray-600"
              :title="t('addDocuments')"
              @click="openAddDocumentsDialog(pageIndex, 'right')"
            >
              <Plus class="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- PDF Preview Modal -->
  <PDFViewer
    v-model:isOpen="showPdfViewer"
    :url="pdfViewerUrl"
    :filename="pdfViewerFilename"
    :document="previewDocumentMeta || undefined"
    :initial-page="1"
    :batch-size="5"
    :fit-page="true"
    :hide-topbar="true"
    :disable-edit-page="true"
    @document-updated="onPdfDocumentUpdated"
  />
</template>

<script setup lang="ts">
  import LazyPDFPageThumbnail from '@/components/pdf/LazyPDFPageThumbnail.vue'
  import PDFViewer from '@/components/pdf/NewPdfViewer.vue'
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
  } from '@/components/ui/dropdown-menu'
  import { useToast } from '@/components/ui/toast'
  import { useTranslation } from '@/composables/useTranslation'
  import { useMetrics, MetricEventType } from '@/composables/useMetrics'
  import {
    ArrowRight,
    ChevronDown,
    Download,
    Eye,
    MoreHorizontal,
    Plus,
    RotateCcwSquareIcon,
    RotateCwSquare,
    Scissors,
    Trash2
  } from 'lucide-vue-next'
  import { computed, onBeforeUnmount, ref, watch, provide, onMounted, onUnmounted } from 'vue'
  import type { DocumentGroup } from './composables/useDocumentOrganization'
  import {
    useDocumentOrganization,
    useDocumentOrganizationContext
  } from './composables/useDocumentOrganization'

  interface Props {
    document: DocumentGroup
    documentIndex: number
    organizedDocuments: DocumentGroup[]
    dragOverTarget: { docIndex: number; pageIndex: number; dropZone?: 'left' | 'right' } | null
    dragOverDocumentGroup: number | null
    isDragging: boolean
    dragMode: 'reorder' | 'move' | null
    dragSourceDocument: number | null
    pageRotations: Map<string, number>
  }

  interface Emits {
    (e: 'documentChange'): void
    (e: 'splitDocument', pageIndex: number): void
    (e: 'openAddDocuments', pageIndex: number, side: 'left' | 'right'): void
    (e: 'dragStart', event: DragEvent, docIndex: number, pageIndex: number): void
    (e: 'dragStartMultiple', event: DragEvent, docIndex: number, pageUuids: string[]): void
    (e: 'dragEnd'): void
    (
      e: 'dragOver',
      event: DragEvent,
      docIndex: number,
      pageIndex: number,
      dropZone?: 'left' | 'right'
    ): void
    (
      e: 'drop',
      event: DragEvent,
      docIndex: number,
      pageIndex: number,
      dropZone?: 'left' | 'right'
    ): void
    (
      e: 'dragEnter',
      event: DragEvent,
      docIndex: number,
      pageIndex: number,
      dropZone?: 'left' | 'right'
    ): void
    (e: 'dragLeave', event: DragEvent, docIndex: number, pageIndex: number): void
    (e: 'createNewDocument', pages: any[]): void
    (e: 'movePages', pages: any[], targetDocName: string): void
    (e: 'deletePages', pages: any[]): void
  }

  const props = defineProps<Props>()
  const emit = defineEmits<Emits>()
  const { toast } = useToast()
  const { trackEvent } = useMetrics()

  const scrollContainerRef = ref<HTMLElement | null>(null)
  const hasStoppedScrolling = ref(true)
  let scrollTimeout: NodeJS.Timeout | null = null

  const handleScroll = () => {
    hasStoppedScrolling.value = false

    if (scrollTimeout) {
      clearTimeout(scrollTimeout)
    }

    scrollTimeout = setTimeout(() => {
      hasStoppedScrolling.value = true
    }, 300)
  }

  onMounted(() => {
    if (scrollContainerRef.value) {
      scrollContainerRef.value.addEventListener('scroll', handleScroll, { passive: true })
    }
  })

  onUnmounted(() => {
    if (scrollContainerRef.value) {
      scrollContainerRef.value.removeEventListener('scroll', handleScroll)
    }
    if (scrollTimeout) {
      clearTimeout(scrollTimeout)
    }
  })

  provide('hasStoppedScrolling', hasStoppedScrolling)

  const { t } = useTranslation({
    en: {
      noPreviewAvailable: 'No preview available',
      rotateLeft: 'Rotate left',
      rotateRight: 'Rotate right',
      previewPage: 'Preview page',
      moreOptions: 'More options',
      downloadPage: 'Download page',
      deletePage: 'Delete page',
      deletePages: 'Delete pages',
      moveTo: 'Move to',
      createNewDocument: 'Create new document',
      page: 'Page',
      splitDocument: 'Split document',
      dropPagesHere: 'Drop pages here to add to {documentName}',
      error: 'Error',
      success: 'Success',
      pageNotFound: 'Page not found',
      newDocument: 'New document',
      cannotSplit: 'Cannot Split',
      documentMustHavePages: 'Document must have at least 2 pages to split',
      cannotSplitLastPage: 'Cannot split after the last page',
      selectAllPages: 'Select all pages',
      selectAtLeastPages: 'Please select at least {count} page(s)',
      pagesDeleted: 'Deleted {count} pages: {pageNames}',
      documentCreated: 'Created {count} new document(s)',
      pagesMoved: 'Moved {count} page(s) to {targetName}',
      addDocuments: 'Add documents',
      documentNotFound: 'Document not found',
      invalidPageIndex: 'Invalid page index',
      cannotMergeDocumentWithItself: 'Cannot merge document with itself',
      cannotMoveAllPages: 'Cannot move all pages from a document. At least one page must remain.',
      cannotMoveSinglePageDocument: 'Cannot move the only page from a single-page document.',
      documentsMergedInto: 'Merged "{sourceName}" into "{targetName}"',
      documentDuplicated: 'Duplicated "{name}"',
      documentDeleted: 'Deleted "{name}"',
      documentNotFoundOrNoPages: 'Document not found or has no pages',
      newDocumentName: 'New {name}',
      mergedDocument: 'Merged Document',
      documentCopyName: '{name} - Copy',
      pageWithNumber: 'Page {number}',
      unknown: 'Unknown',
      documentSplit: 'Document Split',
      documentSplitGroups: 'Split "{name1}" into "{name2}"',
      needMultipleDocuments: 'Need multiple documents to merge',
      documentsMerged: 'All documents merged successfully',
      confirmDelete: 'Are you sure you want to delete this document?',
      pagesRotated: 'Rotated {count} page(s) {direction}',
      pageDeleted: 'Deleted page "{pageName}"'
    },
    de: {
      noPreviewAvailable: 'Keine Vorschau verfügbar',
      rotateLeft: 'Nach links drehen',
      rotateRight: 'Nach rechts drehen',
      previewPage: 'Seite in Vorschau anzeigen',
      moreOptions: 'Weitere Optionen',
      downloadPage: 'Seite herunterladen',
      deletePage: 'Seite löschen',
      deletePages: 'Seiten löschen',
      moveTo: 'Verschieben nach',
      createNewDocument: 'Neues Dokument erstellen',
      page: 'Seite',
      splitDocument: 'Dokument aufteilen',
      dropPagesHere: 'Seiten hier ablegen, um sie zu {documentName} hinzuzufügen',
      error: 'Fehler',
      success: 'Erfolgreich',
      pageNotFound: 'Seite nicht gefunden',
      newDocument: 'Neues Dokument',
      cannotSplit: 'Kann nicht aufteilen',
      documentMustHavePages: 'Dokument muss mindestens 2 Seiten haben, um aufgeteilt zu werden',
      cannotSplitLastPage: 'Kann nicht nach der letzten Seite aufteilen',
      selectAllPages: 'Alle Seiten auswählen',
      selectAtLeastPages: 'Bitte wählen Sie mindestens {count} Seite(n) aus',
      pagesDeleted: '{count} Seiten gelöscht: {pageNames}',
      documentCreated: '{count} neue(s) Dokument(e) erstellt',
      pagesMoved: '{count} Seite(n) nach {targetName} verschoben',
      addDocuments: 'Dokumente hinzufügen',
      documentNotFound: 'Dokument nicht gefunden',
      invalidPageIndex: 'Ungültiger Seitenindex',
      cannotMergeDocumentWithItself: 'Dokument kann nicht mit sich selbst zusammengeführt werden',
      cannotMoveAllPages:
        'Es können nicht alle Seiten aus einem Dokument verschoben werden. Mindestens eine Seite muss verbleiben.',
      cannotMoveSinglePageDocument:
        'Die einzige Seite eines einseitigen Dokuments kann nicht verschoben werden.',
      documentsMergedInto: '"{sourceName}" wurde in "{targetName}" zusammengeführt',
      documentDuplicated: '"{name}" wurde dupliziert',
      documentDeleted: '"{name}" wurde gelöscht',
      documentNotFoundOrNoPages: 'Dokument nicht gefunden oder keine Seiten vorhanden',
      newDocumentName: 'Neues {name}',
      mergedDocument: 'Zusammengeführtes Dokument',
      documentCopyName: '{name} - Kopie',
      pageWithNumber: 'Seite {number}',
      unknown: 'Unbekannt',
      documentSplit: 'Dokument aufgeteilt',
      documentSplitGroups: '"{name1}" wurde in "{name2}" aufgeteilt',
      needMultipleDocuments: 'Mehrere Dokumente erforderlich zum Zusammenführen',
      documentsMerged: 'Alle Dokumente erfolgreich zusammengeführt',
      confirmDelete: 'Sind Sie sicher, dass Sie dieses Dokument löschen möchten?',
      pagesRotated: '{count} Seite(n) nach {direction} gedreht',
      pageDeleted: 'Seite "{pageName}" gelöscht'
    }
  })

  const { downloadSpecificPage, createPdfFromPages, caseId } = useDocumentOrganization(t)

  // Internal selection state
  const selectedPageUuids = ref<string[]>([])

  // PDF preview state
  const showPdfViewer = ref(false)
  const pdfViewerUrl = ref('')
  const pdfViewerFilename = ref('')
  const previewPdfData = ref<Uint8Array | null>(null)

  // Computed properties
  const availableDocumentGroups = computed(() => {
    return props.organizedDocuments.filter((_, index) => index !== props.documentIndex)
  })

  const isAllPagesSelected = computed(() => {
    return (
      props.document.pages.length > 0 &&
      selectedPageUuids.value.length === props.document.pages.length
    )
  })

  const isSomePagesSelected = computed(() => {
    return (
      selectedPageUuids.value.length > 0 &&
      selectedPageUuids.value.length < props.document.pages.length
    )
  })

  // Selection Methods
  const togglePageSelection = (pageUuid: string) => {
    const index = selectedPageUuids.value.indexOf(pageUuid)
    if (index > -1) {
      selectedPageUuids.value.splice(index, 1)
    } else {
      selectedPageUuids.value.push(pageUuid)
    }
  }

  const toggleAllPages = () => {
    if (isAllPagesSelected.value) {
      selectedPageUuids.value = []
    } else {
      selectedPageUuids.value = props.document.pages.map((p: any) => p.uuid)
    }
  }

  const rotateSelectedPagesLeft = () => {
    if (selectedPageUuids.value.length === 0) {
      toast({
        title: t('error'),
        description: t('selectAtLeastPages', { count: 1 }),
        variant: 'destructive'
      })
      return
    }

    selectedPageUuids.value.forEach((pageUuid) => {
      const currentRotation = props.pageRotations.get(pageUuid) || 0
      const newRotation = currentRotation - 90
      props.pageRotations.set(pageUuid, newRotation)
    })

    // Track pages rotated
    trackEvent(MetricEventType.PDF_PAGES_ROTATED, {
      page_count: selectedPageUuids.value.length,
      direction: 'left',
      document_name: props.document.name,
      case_uuid: caseId.value
    })

    emit('documentChange')
  }

  const rotateSelectedPagesRight = () => {
    if (selectedPageUuids.value.length === 0) {
      toast({
        title: t('error'),
        description: t('selectAtLeastPages', { count: 1 }),
        variant: 'destructive'
      })
      return
    }

    selectedPageUuids.value.forEach((pageUuid) => {
      const currentRotation = props.pageRotations.get(pageUuid) || 0
      const newRotation = currentRotation + 90
      props.pageRotations.set(pageUuid, newRotation)
    })

    // Track pages rotated
    trackEvent(MetricEventType.PDF_PAGES_ROTATED, {
      page_count: selectedPageUuids.value.length,
      direction: 'right',
      document_name: props.document.name,
      case_uuid: caseId.value
    })

    emit('documentChange')
  }

  const createNewDocumentGroupWithSelected = () => {
    if (selectedPageUuids.value.length === 0) {
      toast({
        title: t('error'),
        description: t('selectAtLeastPages', { count: 1 }),
        variant: 'destructive'
      })
      return
    }

    // Prevent moving all pages from a document
    if (selectedPageUuids.value.length >= props.document.pages.length) {
      toast({
        title: t('error'),
        description: t('cannotMoveAllPages'),
        variant: 'destructive'
      })
      return
    }

    const selectedPages = props.document.pages.filter((page: any) =>
      selectedPageUuids.value.includes(page.uuid)
    )

    // Track document group created from pages
    trackEvent(MetricEventType.PDF_DOCUMENT_GROUP_CREATED, {
      page_count: selectedPages.length,
      source_document_name: props.document.name,
      case_uuid: caseId.value
    })

    emit('createNewDocument', selectedPages)
    selectedPageUuids.value = []
  }

  const moveSelectedPagesToDocument = (targetDocName: string) => {
    if (selectedPageUuids.value.length === 0) {
      toast({
        title: t('error'),
        description: t('selectAtLeastPages', { count: 1 }),
        variant: 'destructive'
      })
      return
    }

    // Prevent moving all pages from a document
    if (selectedPageUuids.value.length >= props.document.pages.length) {
      toast({
        title: t('error'),
        description: t('cannotMoveAllPages'),
        variant: 'destructive'
      })
      return
    }

    const selectedPages = props.document.pages.filter((page: any) =>
      selectedPageUuids.value.includes(page.uuid)
    )

    // Track pages moved
    trackEvent(MetricEventType.PDF_PAGES_MOVED, {
      page_count: selectedPages.length,
      source_document_name: props.document.name,
      target_document_name: targetDocName,
      case_uuid: caseId.value
    })

    emit('movePages', selectedPages, targetDocName)
    selectedPageUuids.value = []
  }

  const deleteSelectedPages = () => {
    if (selectedPageUuids.value.length === 0) {
      toast({
        title: t('error'),
        description: t('selectAtLeastPages', { count: 1 }),
        variant: 'destructive'
      })
      return
    }

    const selectedPages = props.document.pages.filter((page: any) =>
      selectedPageUuids.value.includes(page.uuid)
    )

    // Track pages deleted
    trackEvent(MetricEventType.PDF_PAGES_DELETED, {
      page_count: selectedPages.length,
      document_name: props.document.name,
      case_uuid: caseId.value
    })

    emit('deletePages', selectedPages)
    selectedPageUuids.value = []
  }

  const { allCaseDocuments } = useDocumentOrganizationContext()

  const getDocumentByUuid = (documentUuid: string) => {
    return allCaseDocuments.value.find((doc: any) => doc.uuid === documentUuid)
  }
  // Individual Page Methods

  const getPageRotation = (pageUuid: string): number => {
    return props.pageRotations.get(pageUuid) || 0
  }

  /**
   * Safe rotation helper that ensures valid rotation values and proper animation direction
   */
  const safeRotateHelper = (pageUuid: string, direction: 'left' | 'right'): void => {
    // Get current rotation with fallback
    const currentRotation = props.pageRotations.get(pageUuid) || 0

    // Calculate new rotation without normalization for smoother animation
    const rotationChange = direction === 'left' ? -90 : 90
    const newRotation = currentRotation + rotationChange

    // Set the new rotation (can be > 360 for smooth animation)
    props.pageRotations.set(pageUuid, newRotation)

    emit('documentChange')
  }

  const rotatePageLeft = (pageUuid: string) => {
    safeRotateHelper(pageUuid, 'left')
  }

  const rotatePageRight = (pageUuid: string) => {
    safeRotateHelper(pageUuid, 'right')
  }

  const previewPage = async (page: any) => {
    if (!page) {
      toast({
        title: t('error'),
        description: 'Page not found',
        variant: 'destructive'
      })
      return
    }

    try {
      // Create a single-page document group for preview
      const singlePageDocument = {
        name: `${t('page')} ${page.actualPageNumber || 1}`,
        pages: [page]
      }

      // Create PDF with progress updates
      const pdfBytes = await createPdfFromPages(
        singlePageDocument.pages,
        props.pageRotations,
        (progress: number) => {
          // Progress callback if needed
        }
      )

      // Clear any existing blob URLs
      if (pdfViewerUrl.value.startsWith('blob:')) {
        URL.revokeObjectURL(pdfViewerUrl.value)
      }

      // Create blob URL for the generated PDF
      const blob = new Blob([pdfBytes], { type: 'application/pdf' })
      const blobUrl = URL.createObjectURL(blob)

      // Set PDF data and URL for the viewer
      previewPdfData.value = pdfBytes
      pdfViewerUrl.value = blobUrl
      pdfViewerFilename.value = singlePageDocument.name
      showPdfViewer.value = true
    } catch (error) {
      console.error('Error generating page preview PDF:', error)
      toast({
        title: t('error'),
        description: 'Failed to generate page preview. Please try again.',
        variant: 'destructive'
      })
    }
  }

  const deleteSpecificPage = (pageUuid: string) => {
    const pageToDelete = props.document.pages.find((page: any) => page.uuid === pageUuid)

    if (!pageToDelete) {
      toast({
        title: t('error'),
        description: t('pageNotFound'),
        variant: 'destructive'
      })
      return
    }

    // Remove page from internal selection if it was selected
    const selectedIndex = selectedPageUuids.value.indexOf(pageUuid)
    if (selectedIndex > -1) {
      selectedPageUuids.value.splice(selectedIndex, 1)
    }

    emit('deletePages', [pageToDelete])
  }

  const createNewDocumentGroupWithPage = (pageUuid: string) => {
    const pageToMove = props.document.pages.find((page) => page.uuid === pageUuid)

    if (!pageToMove) {
      toast({
        title: t('error'),
        description: t('pageNotFound'),
        variant: 'destructive'
      })
      return
    }

    // Prevent moving the only page from a single-page document
    if (props.document.pages.length === 1) {
      toast({
        title: t('error'),
        description: t('cannotMoveSinglePageDocument'),
        variant: 'destructive'
      })
      return
    }

    // Emit event to handle creating new document with this page
    emit('createNewDocument', [pageToMove])
  }

  const movePageToDocument = (pageUuid: string, targetDocName: string) => {
    const pageToMove = props.document.pages.find((page) => page.uuid === pageUuid)

    if (!pageToMove) {
      toast({
        title: t('error'),
        description: t('pageNotFound'),
        variant: 'destructive'
      })
      return
    }

    // Prevent moving the only page from a single-page document
    if (props.document.pages.length === 1) {
      toast({
        title: t('error'),
        description: t('cannotMoveSinglePageDocument'),
        variant: 'destructive'
      })
      return
    }

    emit('movePages', [pageToMove], targetDocName)
  }

  const splitDocument = (pageIndex: number) => {
    if (props.document.pages.length < 2) {
      toast({
        title: t('cannotSplit'),
        description: t('documentMustHavePages'),
        variant: 'destructive'
      })
      return
    }

    if (pageIndex === props.document.pages.length - 1) {
      toast({
        title: t('cannotSplit'),
        description: t('cannotSplitLastPage'),
        variant: 'destructive'
      })
      return
    }

    // Track manual document split
    trackEvent(MetricEventType.PDF_DOCUMENT_SPLIT_MANUAL, {
      document_name: props.document.name,
      split_at_page: pageIndex + 1,
      total_pages: props.document.pages.length,
      pages_before_split: pageIndex + 1,
      pages_after_split: props.document.pages.length - (pageIndex + 1),
      case_uuid: caseId.value
    })

    emit('splitDocument', pageIndex)
  }

  const openAddDocumentsDialog = (pageIndex: number, side: 'left' | 'right') => {
    // Track add documents opened
    trackEvent(MetricEventType.PDF_ADD_DOCUMENTS_OPENED, {
      document_name: props.document.name,
      page_index: pageIndex,
      side: side,
      case_uuid: caseId.value
    })

    emit('openAddDocuments', pageIndex, side)
  }

  // Proximity-based drop zone detection
  const proximityThreshold = 60 // pixels - how close mouse needs to be to highlight drop zone
  const mousePosition = ref({ x: 0, y: 0 })
  const proximityDropZone = ref<{
    docIndex: number
    pageIndex: number
    dropZone: 'left' | 'right'
  } | null>(null)

  // Track mouse position during drag
  const updateMousePosition = (event: DragEvent) => {
    mousePosition.value = { x: event.clientX, y: event.clientY }
  }

  // Find nearest drop zone based on mouse proximity
  const findNearestDropZone = (): {
    docIndex: number
    pageIndex: number
    dropZone: 'left' | 'right'
  } | null => {
    if (!props.isDragging) return null

    const { x: mouseX, y: mouseY } = mousePosition.value
    let bestDistance = Infinity
    let bestZone: { docIndex: number; pageIndex: number; dropZone: 'left' | 'right' } | null = null

    // Check all pages in this document
    props.document.pages.forEach((_, pageIndex) => {
      // Get page element to calculate positions
      const pageElement = document.querySelector(
        `[data-page-uuid="${props.document.pages[pageIndex].uuid}"]`
      ) as HTMLElement
      if (!pageElement) return

      const pageRect = pageElement.getBoundingClientRect()

      // Calculate drop zone positions
      const leftZoneX = pageRect.left - 24 // -left-6 = -24px
      const rightZoneX = pageRect.right + 24 // -right-6 = +24px
      const zoneY = pageRect.top
      const zoneHeight = pageRect.height

      // Check if mouse is within vertical bounds of the page
      if (mouseY >= zoneY && mouseY <= zoneY + zoneHeight) {
        // Check left drop zone (only for first page)
        if (pageIndex === 0) {
          const leftDistance = Math.abs(mouseX - leftZoneX)
          if (leftDistance <= proximityThreshold && leftDistance < bestDistance) {
            bestDistance = leftDistance
            bestZone = {
              docIndex: props.documentIndex,
              pageIndex,
              dropZone: 'left'
            }
          }
        }

        // Check right drop zone
        const rightDistance = Math.abs(mouseX - rightZoneX)
        if (rightDistance <= proximityThreshold && rightDistance < bestDistance) {
          bestDistance = rightDistance
          bestZone = {
            docIndex: props.documentIndex,
            pageIndex,
            dropZone: 'right'
          }
        }
      }
    })

    return bestZone
  }

  // Update proximity drop zone on mouse move
  const updateProximityDropZone = () => {
    if (!props.isDragging) {
      proximityDropZone.value = null
      return
    }

    const nearest = findNearestDropZone()
    proximityDropZone.value = nearest
  }

  // Drag and drop handlers
  const onDragStart = (event: DragEvent, docIndex: number, pageIndex: number) => {
    const draggedPage = props.document.pages[pageIndex]

    // Check if the page being dragged is among the selected pages
    const isDraggedPageSelected = selectedPageUuids.value.includes(draggedPage.uuid)

    // If multiple pages are selected and the dragged page is one of them, start multi-page drag
    if (selectedPageUuids.value.length > 1 && isDraggedPageSelected) {
      emit('dragStartMultiple', event, docIndex, selectedPageUuids.value)
    } else {
      // Single page drag - clear selection if dragging an unselected page
      if (!isDraggedPageSelected) {
        selectedPageUuids.value = []
      }
      emit('dragStart', event, docIndex, pageIndex)
    }
  }

  const onDragEnd = () => {
    // Clear selection after drag ends to reset the UI
    selectedPageUuids.value = []
    emit('dragEnd')
  }

  const calculateDropZone = (event: DragEvent): 'left' | 'right' => {
    const target = event.currentTarget as HTMLElement
    const rect = target.getBoundingClientRect()
    const mouseX = event.clientX
    const elementCenterX = rect.left + rect.width / 2

    return mouseX < elementCenterX ? 'left' : 'right'
  }

  // Drop zone specific handlers
  const onDropZoneDragOver = (
    event: DragEvent,
    docIndex: number,
    pageIndex: number,
    dropZone: 'left' | 'right'
  ) => {
    event.preventDefault()
    updateMousePosition(event)
    updateProximityDropZone()

    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move'
    }
    emit('dragOver', event, docIndex, pageIndex, dropZone)
  }

  const onDropZoneDragEnter = (
    event: DragEvent,
    docIndex: number,
    pageIndex: number,
    dropZone: 'left' | 'right'
  ) => {
    event.preventDefault()
    emit('dragEnter', event, docIndex, pageIndex, dropZone)
  }

  const onDropZoneDragLeave = (
    event: DragEvent,
    docIndex: number,
    pageIndex: number,
    dropZone: 'left' | 'right'
  ) => {
    event.preventDefault()
    emit('dragLeave', event, docIndex, pageIndex)
  }

  const onDropZoneDrop = (
    event: DragEvent,
    docIndex: number,
    pageIndex: number,
    dropZone: 'left' | 'right'
  ) => {
    event.preventDefault()
    emit('drop', event, docIndex, pageIndex, dropZone)
  }

  // Enhanced drop handler that checks proximity if exact drop zone isn't hit
  const onDocumentDrop = (event: DragEvent) => {
    event.preventDefault()

    // First try to use proximity detection if available
    if (proximityDropZone.value) {
      const proximityZone = proximityDropZone.value
      emit('drop', event, proximityZone.docIndex, proximityZone.pageIndex, proximityZone.dropZone)
      return
    }

    // If no proximity zone, don't handle the drop (let other handlers take over)
  }

  // Global mouse move listener for proximity detection
  const handleGlobalMouseMove = (event: MouseEvent) => {
    if (props.isDragging) {
      mousePosition.value = { x: event.clientX, y: event.clientY }
      updateProximityDropZone()
    }
  }

  // Add/remove global mouse move listener
  const addGlobalMouseListener = () => {
    document.addEventListener('mousemove', handleGlobalMouseMove)
  }

  const removeGlobalMouseListener = () => {
    document.removeEventListener('mousemove', handleGlobalMouseMove)
  }

  // Watch for drag state changes to add/remove listeners
  watch(
    () => props.isDragging,
    (isDragging) => {
      if (isDragging) {
        addGlobalMouseListener()
      } else {
        removeGlobalMouseListener()
        proximityDropZone.value = null
      }
    }
  )

  // Cleanup on unmount
  onBeforeUnmount(() => {
    removeGlobalMouseListener()
  })
</script>
